import PropTypes from "prop-types";
import Skeleton from "react-loading-skeleton";

import Flex from "../../Flex";
import styles from "./styles";
import Typography from "../../Typography";
import { useMemo } from "react";
import { friendlyNumber } from "common/utilities";

const Slider = ({
  id = "",
  label = "",
  // `variant` is what Field passes down; `size` stays as the direct-use prop.
  variant = "large",
  size = variant,
  isRange = false,
  isDisabled = false,
  isLoading = false,
  value,
  minValue = 0,
  maxValue = 100,
  increment = 1,
  inverted,
  orientation,
  minStepsBetweenThumbs = 1,
  onChange,
  isReadOnly = false,
  dataTest = "slider",
}) => {
  const isSmall = size === "small";
  const rangeMin = value?.min ?? minValue;
  const rangeMax = value?.max ?? maxValue;
  const tickFontSize = isSmall ? "10px" : "12px";
  // role="slider" lives on the thumbs, so that is where read-only belongs.
  const thumbProps = { size, "aria-readonly": isReadOnly || undefined };

  const filledTrackStyle = useMemo(() => {
    const span = maxValue - minValue || 1;

    return isRange
      ? {
          left: `${((rangeMin - minValue) / span) * 100}%`,
          width: `${((rangeMax - rangeMin) / span) * 100}%`,
        }
      : {
          width: `${(((value ?? minValue) - minValue) / span) * 100}%`,
        };
  }, [isRange, maxValue, minValue, rangeMax, rangeMin, value]);

  const onValueChange = (newValue) => {
    if (isRange) {
      onChange({
        min: Math.min(...newValue),
        max: Math.max(...newValue),
      });
    } else {
      onChange(newValue[0]);
    }
  };

  return (
    <styles.InputGroup
      data-test={dataTest}
      disabled={isDisabled}
      isReadOnly={isReadOnly}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        width: isSmall ? "100px" : "150px",
      }}
    >
      {!isSmall && label && <styles.Label>{label}</styles.Label>}

      {isLoading ? (
        <Skeleton />
      ) : (
        <>
          <Flex direction="row" alignItems="center">
            <styles.Root
              id={id}
              data-test={`${dataTest}-root`}
              disabled={isDisabled}
              value={isRange ? [rangeMin, rangeMax] : [value ?? minValue]}
              min={minValue}
              max={maxValue}
              inverted={inverted}
              orientation={orientation}
              step={increment}
              minStepsBetweenThumbs={minStepsBetweenThumbs}
              onValueChange={onValueChange}
            >
              <styles.Track size={size} data-test={`${dataTest}-track`}>
                <styles.FilledTrack
                  style={filledTrackStyle}
                  size={size}
                  data-test={`${dataTest}-filled-track`}
                />
                <styles.Range />
              </styles.Track>

              {isRange ? (
                <>
                  <styles.Thumb
                    {...thumbProps}
                    aria-label={label ? `${label} minimum` : undefined}
                    data-test={`${dataTest}-thumb-min`}
                  >
                    {friendlyNumber(rangeMin)}
                  </styles.Thumb>
                  <styles.Thumb
                    {...thumbProps}
                    aria-label={label ? `${label} maximum` : undefined}
                    data-test={`${dataTest}-thumb-max`}
                  >
                    {friendlyNumber(rangeMax)}
                  </styles.Thumb>
                </>
              ) : (
                <styles.Thumb
                  {...thumbProps}
                  aria-label={label || undefined}
                  data-test={`${dataTest}-thumb`}
                >
                  {friendlyNumber(value ?? minValue)}
                </styles.Thumb>
              )}
            </styles.Root>
          </Flex>

          <Flex direction="row" justifyContent="space-between">
            <Typography
              variant="sub-text"
              noMargin
              style={{
                width: "33%",
                fontSize: tickFontSize,
              }}
            >
              {friendlyNumber(minValue)}
            </Typography>
            <Typography
              variant="sub-text"
              noMargin
              style={{
                width: "33%",
                textAlign: "center",
                fontSize: tickFontSize,
              }}
            >
              {friendlyNumber((maxValue - minValue) * 0.5 + minValue)}
            </Typography>
            <Typography
              variant="sub-text"
              noMargin
              style={{
                width: "33%",
                textAlign: "right",
                fontSize: tickFontSize,
              }}
            >
              {friendlyNumber(maxValue)}
            </Typography>
          </Flex>
        </>
      )}
    </styles.InputGroup>
  );
};

Slider.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  dataTest: PropTypes.string,
  variant: PropTypes.oneOf(["small", "large"]),
  size: PropTypes.oneOf(["small", "large"]),
  isLoading: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isRange: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  increment: PropTypes.number,
  inverted: PropTypes.bool,
  orientation: PropTypes.oneOf(["horizontal", "vertical"]),
  minStepsBetweenThumbs: PropTypes.number,
  value: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.shape({
      min: PropTypes.number,
      max: PropTypes.number,
    }),
  ]),
  minValue: PropTypes.number,
  maxValue: PropTypes.number,
  onChange: PropTypes.func.isRequired,
};

export default Slider;
